import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { getLoyaltyBlockedStatus } from 'src/scripts/templates/_shared/loyaltyBlockedStatus';
import { IS_NATIVE_APP_CLIENT, pluralize } from 'src/scripts/utils/helpers';
import { GWPandLoyaltyGeoSettings } from '../../../../../globals';
import { usePromise } from '../../../../../templates/_shared/react-hooks';
import { openSignInModal } from '../../../../../templates/_shared/ui-components/SignInModal/utils';
import { fireHeapOpenSignInsignUpModalEvent } from '../../../../../utils/heap-events';
import { loyaltyState } from './loyaltyState';
import { useMediaQuery } from '@material-ui/core';
import { MUI_DESKTOP_BREAKPOINT } from 'src/scripts/utils/constants';
import { prependLocaleToPath } from '../../../../../utils/localization';

let LoyaltyTopPanel = () => {
  const { data, isLoading } = usePromise(() => GWPandLoyaltyGeoSettings());
  const [customerData, setCustomerData] = useState<CustomerInfo | null>(null);
  const [isUserBlocked, setUserBlockedStatus] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const isDesktop = useMediaQuery(MUI_DESKTOP_BREAKPOINT, { noSsr: true });
  const isMobileAndTablet = !isDesktop;

  const pointsBalance = toJS(loyaltyState.pointsBalance);
  const loyaltyTier = toJS(loyaltyState.tier);

  const tierInlineLogoMobile: Record<string, string> = {
    tier1: 'https://cdn.shopify.com/s/files/1/2185/2813/files/vip_logo_2025.png?v=1746217500',
    tier2: 'https://cdn.shopify.com/s/files/1/2185/2813/files/a-list_logo_2025.png?v=1746217197',
    tier3: 'https://cdn.shopify.com/s/files/1/2185/2813/files/all-access_logo_2025.png?v=1746214886',
  };

  const tierInlineLogoDesktop: Record<string, string> = {
    tier1: 'https://cdn.shopify.com/s/files/1/2185/2813/files/vip_aloaccess_blk_1.svg?v=1696582458',
    tier2: 'https://cdn.shopify.com/s/files/1/2185/2813/files/alist_aloaccess_blk_1.svg?v=1696582628',
    tier3: 'https://cdn.shopify.com/s/files/1/2185/2813/files/allaccess_aloaccess_blk_1.svg?v=1696582674',
  };
  const tierData = isMobileAndTablet
    ? loyaltyTier.length > 0
      ? tierInlineLogoMobile[loyaltyTier]
      : tierInlineLogoMobile['tier1']
    : loyaltyTier.length > 0
    ? tierInlineLogoDesktop[loyaltyTier]
    : tierInlineLogoDesktop['tier1'];
  useEffect(() => {
    if (window.getCustomerInfo) {
      setCustomerData(window.getCustomerInfo());
    }
    const getUserStatus = async () => {
      setUserBlockedStatus(await getLoyaltyBlockedStatus());
    };
    getUserStatus();
  }, []);

  const [currentIconWidth, setIconWidth] = useState(30);

  const isCustomer = Boolean(customerData?.isCustomer);

  if (
    IS_NATIVE_APP_CLIENT ||
    isLoading ||
    (!data?.shouldRenderLoyaltyForDomestic && !data?.shouldRenderLoyaltyWidgetForINTL) ||
    isUserBlocked ||
    (isMobileAndTablet && window.location.pathname.includes('access'))
  )
    return null;

  const goToAccountPage = () => {
    window.location.href = prependLocaleToPath('/account');
  };

  const goToAccess = () => {
    window.location.href = data?.shouldRenderLoyaltyForDomestic
      ? prependLocaleToPath('/pages/access')
      : prependLocaleToPath('/pages/in-access');
  };

  const goToPage = () => {
    if (isCustomer) return goToAccountPage();
    else {
      fireHeapOpenSignInsignUpModalEvent({
        ctaLocation: 'Loyalty Top banner Desktop',
        ctaName: 'SIGN IN TO GET REWARDS',
      });
      return openSignInModal();
    }
  };

  const panelAttribsForCustomer = () => {
    return {
      role: 'link',
      ariaLabel: 'Go to Access Page',
      onClick: goToPage,
      onKeydown: (e: { key: string }) => {
        if (e.key === 'Enter') {
          goToPage();
        }
      },
      tabIndex: 0,
      style: { cursor: 'pointer' },
    };
  };

  const topPanelSettings = window.loyalty_settings && window.loyalty_settings.topPanelSettings;
  const discountCodeFromSettings = window.loyalty_settings && window.loyalty_settings.code;
  const shouldRenderTopPanelMessage =
    isCustomer && topPanelSettings?.message_enabled && topPanelSettings.message.length > 0 && discountCodeFromSettings;

  return (
    <div className={`loyalty-top-panel ${!isMobileAndTablet ? 'loyalty-info' : ''} ${!isCustomer ? `guest-mode` : ''}`}>
      {isMobileAndTablet ? (
        <div className={`left-part`} {...panelAttribsForCustomer()}>
          <p className="tag-small uppercase white">
            {isCustomer ? (
              <span className="name-format">
                Hi{' '}
                {customerData?.firstName ? (
                  <span className="customer-name GlobalLinkNoTx">{customerData.firstName}!</span>
                ) : (
                  <span>there!</span>
                )}
              </span>
            ) : (
              `Get rewards with`
            )}
          </p>
          {shouldRenderTopPanelMessage ? (
            <p>
              {topPanelSettings?.message} {discountCodeFromSettings}
            </p>
          ) : null}
          {!isCustomer && isMobileAndTablet && (
            <img
              className="img-fluid"
              src="https://cdn.shopify.com/s/files/1/2185/2813/files/alo-access_logo_2025.png?v=1746212864"
              alt=""
            />
          )}
        </div>
      ) : (
        /* Hover on Account icon and content, underline on greeting only */
        <div
          className={`loyalty-name-part ${!isCustomer ? 'hover-underline' : ''}`}
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
          style={{
            borderBottomColor: isHover && isCustomer ? 'var(--primary)' : '',
            cursor: 'pointer',
          }}
        >
          <button aria-label="Account" onClick={goToPage} data-logged-out="true" className="account-icon">
            <span className={isCustomer || isHover ? 'icon-account-solid' : 'icon-account-static'}></span>
          </button>
          <div className="left-part" {...panelAttribsForCustomer()}>
            <p className="banner">
              {isCustomer ? (
                <span className="semibold name-format">
                  Hi{' '}
                  {customerData?.firstName ? (
                    <span className="customer-name primary-color GlobalLinkNoTx">{customerData.firstName}!</span>
                  ) : (
                    <span>there!</span>
                  )}
                </span>
              ) : (
                <span>SIGN IN TO GET REWARDS</span>
              )}
            </p>
          </div>
        </div>
      )}
      {/* [REVISIT] Attach sign-in modal opener handler */}
      {!isCustomer ? (
        isMobileAndTablet && (
          <button
            type="button"
            className="right-part cta-small white"
            onClick={() => {
              fireHeapOpenSignInsignUpModalEvent({
                ctaLocation: 'Loyalty Top banner',
                ctaName: 'Start Now',
              });
              openSignInModal();
            }}
          >
            Sign in
          </button>
        )
      ) : pointsBalance > 0 ? (
        <div
          className="balance-display"
          onClick={goToAccess}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              goToAccess();
            }
          }}
          tabIndex={0}
        >
          <img
            style={{ width: isMobileAndTablet ? currentIconWidth : '' }}
            onLoad={(e) => {
              const width = (e.currentTarget?.naturalWidth || 0) / 2 + 5; // [REVISIT] Each icon requires custom sizing. Since the image is 2x we are dividing by 2 and adding 4px;
              if (width) setIconWidth(width);
            }}
            src={tierData}
            alt={loyaltyTier.length > 0 ? loyaltyTier : 'tier1'}
            className="tier-logo-inline"
          />
          <p className={`points ${!isMobileAndTablet ? 'body-small primary-color' : 'tag tag-small uppercase'}`}>
            | {pluralize(pointsBalance, 'PT')}
          </p>
        </div>
      ) : null}
    </div>
  );
};

LoyaltyTopPanel = observer(LoyaltyTopPanel);
export { LoyaltyTopPanel };

const renderMobileTopLoyaltyPanel = () => {
  const mountNode = document.querySelectorAll("[react-render-target='loyalty-top-panel']");
  if (mountNode.length > 0) {
    return mountNode.forEach((node) => ReactDOM.render(<LoyaltyTopPanel />, node));
  } else {
    console.warn('missing render targets: loyalty-top-panel');
  }
};

export const InitMobileTopLoyaltyPanel = () => {
  renderMobileTopLoyaltyPanel();
};
